import moment from "moment-timezone";
import {createElement} from "react";
import {ApiError} from "../../api/artifacts";
import {FaBanIcon, FaCheckCircleIcon} from "../../models/Icons.model";
import {
  CLOCK_STATION_MODEL_OPTIONS,
  AppChangelogFormState,
  AppChangelogItem,
  ClockStationFirmwareFormState,
  EspFirmwareFormState,
  FirmwareFormState,
  FirmwareItem,
  FirmwareModalMode,
} from "./Firmwares.model";
import type {EspType} from "../../api/artifacts";

export const FRONT_FILTER_PAGE_SIZE = 10000;
const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

export class FirmwaresUtils {
  static getMissingRequiredFields = (fields: Array<{label: string; value: unknown}>): string[] => {
    return fields
      .filter(({value}) => {
        if (value === null || value === undefined) return true;
        if (typeof value === "string") return !value.trim();

        return false;
      })
      .map(({label}) => label);
  };

  static getRequiredFieldsMessage = (labels: string[]) => {
    if (labels.length === 1) {
      return `Заполните поле: ${labels[0]}`;
    }

    return `Заполните поля: ${labels.join(", ")}`;
  };

  static formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = moment(value);
    return date.isValid() ? date.format(DATE_TIME_FORMAT) : value;
  };

  static formatFileSize = (value?: number) => {
    if (!value) return "-";
    if (value < 1024) return `${value} Б`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} КБ`;
    return `${(value / 1024 / 1024).toFixed(1)} МБ`;
  };

  static getModalTitle = (mode: FirmwareModalMode) => {
    return mode === "create" ? "Добавить прошивку" : "Редактировать прошивку";
  };

  static getSuccessMessage = (mode: FirmwareModalMode) => {
    return mode === "create" ? "Прошивка добавлена" : "Прошивка обновлена";
  };

  static getErrorMessage = (error: unknown, mode: FirmwareModalMode) => {
    if (error instanceof ApiError) {
      const accessError = FirmwaresUtils.getAccessErrorMessage(error.status, "сохранения прошивки");

      return accessError || `Ошибка сохранения прошивки: ${error.status}`;
    }

    return mode === "create" ? "Ошибка добавления прошивки" : "Ошибка обновления прошивки";
  };

  static getListErrorMessage = (error: unknown, entityName = "прошивок") => {
    if (error instanceof ApiError) {
      const accessError = FirmwaresUtils.getAccessErrorMessage(error.status, `получения ${entityName}`);

      return accessError || `Ошибка получения ${entityName}: ${error.status}`;
    }

    return `Ошибка получения ${entityName}`;
  };

  static getItems = <T>(response: unknown): T[] => {
    if (Array.isArray(response)) return response as T[];

    if (response && typeof response === "object") {
      const responseObject = response as Record<string, unknown>;

      if (Array.isArray(responseObject.items)) return responseObject.items as T[];
      if (Array.isArray(responseObject.data)) return responseObject.data as T[];
    }

    return [];
  };

  static getFirmwareInitialState = (firmware?: FirmwareItem | null): FirmwareFormState => ({
    version: firmware?.version || "",
    description: firmware?.description || "",
    deviceType: firmware?.deviceType || null,
    file: null,
  });

  static getEspFirmwareInitialState = (firmware?: {version?: string | null; description?: string | null; espType?: EspType} | null): EspFirmwareFormState => ({
    version: firmware?.version ?? null,
    description: firmware?.description || "",
    espType: FirmwaresUtils.getEspType(firmware?.espType),
    file: null,
  });

  static getClockStationFirmwareInitialState = (
    firmware?: {
      version?: string | null;
      description?: string | null;
      model?: string | null;
      modelType?: EspType;
      channels?: number | null;
      melody?: boolean | null;
      customMelodyEffect?: boolean | null;
    } | null
  ): ClockStationFirmwareFormState => ({
    version: firmware?.version ?? null,
    description: firmware?.description || "",
    model: firmware?.model || "",
    modelType: FirmwaresUtils.getEspType(firmware?.modelType),
    channels: firmware?.channels ?? null,
    melody: typeof firmware?.melody === "boolean" ? firmware.melody : null,
    customMelodyEffect: typeof firmware?.customMelodyEffect === "boolean" ? firmware.customMelodyEffect : null,
    file: null,
  });

  static getAppChangelogInitialState = (changelog?: AppChangelogItem | null): AppChangelogFormState => ({
    version: changelog?.version || "",
    description: changelog?.description || "",
    platform: changelog?.platform || null,
  });

  static getEspType = (value?: string | null): EspType | null => {
    if (value === "Undefined" || value === "Almaaz" || value === "Atlas" || value === "Astra" || value === "Atria") {
      return value;
    }

    return null;
  };

  static formatEspType = (value?: string | null) => {
    if (!value) return "-";

    return value;
  };

  static getSearchString = (value: unknown): string => {
    if (value === null || value === undefined) return "";

    if (Array.isArray(value)) {
      return value.map(FirmwaresUtils.getSearchString).join(" ");
    }

    if (typeof value === "object") {
      return Object.values(value).map(FirmwaresUtils.getSearchString).join(" ");
    }

    return String(value);
  };

  static formatClockStationModel = (value?: string | null) => {
    if (!value) return "-";

    return CLOCK_STATION_MODEL_OPTIONS.find((option) => option.value === value)?.label || value;
  };

  static downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  static DeployStateIcon = ({enabled}: {enabled?: boolean}) => {
    return enabled
      ? createElement(FaCheckCircleIcon, {color: "#2f9e44", size: 18})
      : createElement(FaBanIcon, {color: "#8c8c8c", size: 18});
  };

  private static getAccessErrorMessage = (status: number, actionText: string) => {
    if (status === 401) return `Нет авторизации для ${actionText}`;
    if (status === 403) return `Нет прав для ${actionText}`;

    return "";
  };
}
